import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Search, Users } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { EmptyState, Spinner, useToast } from "@/components/earnos/kit";
import { colors, statusColor, money } from "@/lib/theme";

const FILTERS = ["All", "New", "Contacted", "Replied", "Proposal sent", "Won", "Lost", "Paused"];

export default function Leads() {
  const navigate = useNavigate();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const d = await apiFetch("/leads");
      setLeads(d.leads);
    } catch (e) {
      setLoadError(e.message || "Service unavailable. Please retry.");
      toast.show(e?.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const matchFilter = filter === "All" || l.status === filter;
      const q = query.trim().toLowerCase();
      const matchQuery = !q || l.business_name?.toLowerCase().includes(q) || l.contact_name?.toLowerCase().includes(q) || l.industry?.toLowerCase().includes(q);
      return matchFilter && matchQuery;
    });
  }, [leads, filter, query]);

  if (loadError && !loading) return <LoadError title="Leads" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} data-testid="leads-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
          <h1 className="text-3xl font-extrabold tracking-tight text-ink">Leads</h1>
        </div>
        <button onClick={() => navigate("/lead/new")} data-testid="add-lead-button" className="inline-flex items-center gap-1 h-10 px-4 rounded-full bg-brand text-white font-bold hover:brightness-110">
          <Plus size={20} /> Add
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-2 px-3 h-12 mt-5 max-w-xl">
        <Search size={18} className="text-ink-2" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search leads..." data-testid="lead-search" className="flex-1 bg-transparent text-base text-ink placeholder:text-ink-2 outline-none" />
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`h-9 px-4 rounded-full text-base font-semibold border transition-colors ${filter === f ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3 hover:border-line-strong"}`}>{f}</button>
        ))}
      </div>

      {loading ? (
        <Spinner className="py-24" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={leads.length === 0 ? "No leads yet" : "No matching leads"}
          message={leads.length === 0 ? "Your Income Engine will help you decide who to contact first." : "Try a different search or filter."}
          actionTitle={leads.length === 0 ? "Add Your First Lead" : undefined}
          onAction={() => navigate("/lead/new")}
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-3 mt-5">
          {filtered.map((l) => (
            <button key={l.lead_id} onClick={() => navigate(`/lead/${l.lead_id}`)} data-testid={`lead-${l.lead_id}`} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4 hover:border-line-strong transition-colors text-left">
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-brand-light text-lg font-extrabold shrink-0" style={{ backgroundColor: colors.brandTertiary + "33" }}>
                {(l.business_name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-ink truncate">{l.business_name}</p>
                <p className="text-sm text-ink-2 truncate">{l.industry || l.contact_name || "—"}</p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="px-2 py-0.5 rounded-full text-sm font-bold" style={{ color: statusColor[l.status] || colors.brandSecondary, backgroundColor: (statusColor[l.status] || colors.brandSecondary) + "22" }}>{l.status}</span>
                {l.estimated_value ? <span className="text-sm text-ink-3 font-semibold">{money(l.estimated_value)}</span> : null}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
